/* eslint-disable react/jsx-no-useless-fragment */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Skeleton, Text } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { FC, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useClipboard } from '@mantine/hooks';
import { EventPortalContext } from '../../Service/Context/EventPortalContext';
import {
  deleteResource,
  fetchResourceByEntityTypeAndEntityId,
  newShare,
  share,
} from '../../Service/restapi/shareService';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { LicenceS } from '../../Service/LicenceS';
import { PortalResouce } from '../../Types/ShareT';
import { PlaceholderS } from '../../Service/PlaceholderS';
import DocumentResourceBadge from './Documents/DocumentResourceBadge';
import { ToastS } from '../../Service/ToastS';

const DocumentPortalStatus: FC<{
  entityType: string;
  entityId: string;
  shortVersion?: boolean;
  setDraft: (val: boolean) => void;
  setSigned?: (val: boolean) => void;
  label?: string;
}> = ({ entityType, entityId, shortVersion, setDraft, setSigned, label }) => {
  const { pathname } = useLocation();
  const { i18n } = useLingui();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { shares, reload } = useContext(EventPortalContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [portalResources, setPortalResources] = useState<PortalResouce[]>([]);
  const { length } = portalResources;
  const { id: eventId } = useParams();
  const clipboard = useClipboard();

  const publish = async () => {
    executeWithLicenceCheck(LicenceS.Restrictions.PORTAL, async () => {
      const placeholdersResolved = await PlaceholderS.getUnresolvedPlaceholders(entityId, entityType);
      if (placeholdersResolved) {
        createShare();
      }
    });
  };

  const createShare = () => {
    setLoading(true);
    const requestBody = [
      { entityType, entityId, label: label ?? i18n._(`resource.entityType.${entityType.toLowerCase()}`) },
    ];

    if (shares.length > 0) {
      share(shares[0].id, { entityTypeAndId: requestBody })
        .then((resp) => {
          if (resp.ok) {
            fetchPortalResource(true);
            setDraft(false);
          }
        })
        .finally(() => setLoading(false));
    } else {
      newShare(undefined, undefined, undefined, eventId).then((resp) => {
        if (resp.ok) {
          resp.json().then((s) => {
            share(s.id, { entityTypeAndId: requestBody })
              .then((resp) => {
                if (resp.ok) {
                  fetchPortalResource(true);
                  setDraft(false);
                }
              })
              .finally(() => {
                reload();
                setLoading(false);
              });
          });
        }
      });
    }
  };

  const fetchPortalResource = async (withClipboard = false) => {
    setLoading(false);
    const resp = await fetchResourceByEntityTypeAndEntityId(entityType, entityId);
    setLoading(false);
    if (resp.ok) {
      const resJSON = await resp.json();
      if (resJSON) {
        setPortalResources(resJSON);
        if (withClipboard) {
          ToastS.info('link-copied', 'Portal-Link in the Zwischenablage kopiert');
          clipboard.copy(resJSON[0].digitalSignLink);
        }
      }
    }
  };

  const unpublish = async () => {
    const resp = await deleteResource(portalResources[0].id);
    if (resp.ok) {
      setPortalResources([]);
      if (reload) reload();
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchPortalResource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <Skeleton width={100} height={20} radius="md" />
      ) : (
        <>
          {length === 0 ? (
            <>
              {pathname.includes('events') && (
                <Text
                  align="center"
                  size="xs"
                  weight="lighter"
                  sx={(theme) => ({
                    cursor: 'pointer',
                    color: theme.colorScheme === 'dark' ? theme.colors.blue[1] : theme.colors.blue[4],
                  })}
                  onClick={publish}
                >
                  <u>Jetzt freigeben</u>
                </Text>
              )}
            </>
          ) : (
            <>
              {portalResources.map((_resource) => {
                const { id } = _resource;
                return (
                  <DocumentResourceBadge
                    key={id}
                    id={id}
                    shortVersion={shortVersion}
                    entityType={entityType}
                    setSigned={setSigned}
                    onDelete={unpublish}
                  />
                );
              })}
            </>
          )}
        </>
      )}
    </>
  );
};

export default DocumentPortalStatus;
